import { useMemo } from 'react';
import { palette } from '../../styles/tokens';
import type { ScheduleEntry, ScheduleEntryType } from '../../types';
import { extractDate, ENTRY_TYPE_COLORS } from './scheduleHelpers';

interface MonthViewProps {
  year: number;
  month: number;
  entries: ScheduleEntry[];
  onDayClick: (date: Date) => void;
}

const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface DayData {
  dateStr: string;
  day: number;
  isCurrentMonth: boolean;
  counts: Record<string, number>;
  hasGap: boolean;
  total: number;
}

export function MonthView({ year, month, entries, onDayClick }: MonthViewProps) {
  const todayStr = useMemo(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
  }, []);

  const weeks = useMemo(() => {
    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0);
    const dow = firstOfMonth.getDay();
    const startOffset = dow === 0 ? 6 : dow - 1;
    const gridStart = new Date(firstOfMonth);
    gridStart.setDate(gridStart.getDate() - startOffset);

    const countMap = new Map<string, Record<string, number>>();
    const gapSet = new Set<string>();
    for (const e of entries) {
      if (e.status === 'cancelled') { gapSet.add(extractDate(e.start_at)); continue; }
      const d = extractDate(e.start_at);
      const counts = countMap.get(d) ?? {};
      counts[e.type] = (counts[e.type] ?? 0) + 1;
      countMap.set(d, counts);
    }

    const result: DayData[][] = [];
    const cur = new Date(gridStart);
    for (let w = 0; w < 6; w++) {
      const week: DayData[] = [];
      for (let d = 0; d < 7; d++) {
        const ds = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
        const counts = countMap.get(ds) ?? {};
        const total = Object.values(counts).reduce((a, b) => a + b, 0);
        week.push({ dateStr: ds, day: cur.getDate(), isCurrentMonth: cur.getMonth() === month, counts, hasGap: gapSet.has(ds), total });
        cur.setDate(cur.getDate() + 1);
      }
      result.push(week);
      if (cur > lastOfMonth && cur.getDay() === 1) break;
    }
    return result;
  }, [year, month, entries]);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: `1px solid ${palette.g150}` }}>
        {DAY_HEADERS.map(d => (
          <div key={d} style={{
            padding: '8px 0', textAlign: 'center', fontSize: 11, fontWeight: 600,
            color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>{d}</div>
        ))}
      </div>

      {weeks.map((week, wi) => (
        <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {week.map(day => {
            const isToday = day.dateStr === todayStr;
            return (
              <button key={day.dateStr} onClick={() => onDayClick(new Date(day.dateStr + 'T12:00:00'))} style={{
                minHeight: 80, padding: '6px 8px', border: 'none', cursor: 'pointer',
                borderBottom: `1px solid ${palette.g100}`, borderRight: `1px solid ${palette.g100}`,
                background: isToday ? palette.accentWash : 'transparent',
                borderLeft: day.hasGap ? `3px solid ${palette.red}` : 'none',
                textAlign: 'left', fontFamily: 'inherit', opacity: day.isCurrentMonth ? 1 : 0.4,
                transition: 'background 0.1s',
              }}
                onMouseEnter={e => { if (!isToday) e.currentTarget.style.background = palette.g50; }}
                onMouseLeave={e => { if (!isToday) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ fontSize: 13, fontWeight: isToday ? 700 : 500, color: isToday ? palette.accent : palette.g700, marginBottom: 4 }}>
                  {day.day}
                </div>
                <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {Object.entries(day.counts).map(([type, count]) => {
                    const color = ENTRY_TYPE_COLORS[type as ScheduleEntryType] ?? palette.g300;
                    return Array.from({ length: Math.min(count, 5) }, (_, i) => (
                      <div key={`${type}-${i}`} style={{ width: 6, height: 6, borderRadius: 3, background: color }} />
                    ));
                  })}
                </div>
                {day.total > 0 && (
                  <div style={{ fontSize: 10, color: palette.g400, marginTop: 4 }}>
                    {day.total} item{day.total > 1 ? 's' : ''}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
